import { useState, useEffect, useContext } from "react";
import { FlashbarProps } from "@amzn/awsui-components-react";
import { IContentEvaluation, EntityType, EntityRef, ContentEvaluationConfiguration } from "@amzn/ask-legal-domain";
import { useAPI } from "./api-hook";
import { AppContext } from "../setup/context";
import { APIResponse } from "../api/common";

export const useContentEvaluation = (pageId?: string) => {
    const context = useContext(AppContext);
    const [evaluationResults, setEvaluationResults] = useState<IContentEvaluation.SubmitContentEvaluationOutput | null>(null);
    const [flashbarProps, setFlashbarProps] = useState<Pick<FlashbarProps, "items">>();
    const [contentEvaluationConfig, setContentEvaluationConfig] = useState<ContentEvaluationConfiguration.Data | null>(null);
    const [isLoadingConfig, setIsLoadingConfig] = useState<boolean>(false);

    const submitContentEvaluationRunner = useAPI(
        context.getContentEvaluationConfigurationAPI().submitContentEvaluation
    );
    const loadContentEvaluationConfigRunner = useAPI(
        context.getContentEvaluationConfigurationAPI().load
    );

    // Load content evaluation configuration
    useEffect(() => {
        if (pageId) {
            setIsLoadingConfig(true);
            loadContentEvaluationConfigRunner.submitRun(pageId);
        }
    }, [pageId]);

    // Handle content evaluation configuration load result
    useEffect(() => {
        if (loadContentEvaluationConfigRunner.status === "Succeeded") {
            setContentEvaluationConfig(loadContentEvaluationConfigRunner.data.output);
            setIsLoadingConfig(false);
        } else if (loadContentEvaluationConfigRunner.status === "Error") {
            if (loadContentEvaluationConfigRunner.data?.err?.code === 404) {
                setContentEvaluationConfig(null);
            } else {
                const errorMessage = loadContentEvaluationConfigRunner.data?.err?.message || "Unknown error";
                setFlashbarProps({
                    items: [{
                        type: "error",
                        content: `Failed to load content evaluation configuration: ${errorMessage}`,
                        dismissible: true,
                        onDismiss: () => setFlashbarProps(undefined)
                    }]
                });
            }
            setIsLoadingConfig(false);
        }
    }, [loadContentEvaluationConfigRunner.status]);

    // Handle content evaluation result
    useEffect(() => {
        if (submitContentEvaluationRunner.status === "Succeeded") {
            setEvaluationResults(submitContentEvaluationRunner.data.output);
            setFlashbarProps(undefined);
        } else if (submitContentEvaluationRunner.status === "Error") {
            const errorMessage = submitContentEvaluationRunner.data?.err?.message || "Unknown error";
            setFlashbarProps({
                items: [{
                    type: "error",
                    content: `Failed to evaluate content: ${errorMessage}`,
                    dismissible: true,
                    onDismiss: () => setFlashbarProps(undefined)
                }]
            });
        }
    }, [submitContentEvaluationRunner.status]);

    const submitSingleEvaluation = (entityRef: EntityRef, contentText: string) => {
        setEvaluationResults(null);
        setFlashbarProps(undefined);

        submitContentEvaluationRunner.submitRun(
            IContentEvaluation.SubmitContentEvaluationInput.create({
                entityRef,
                contentText
            })
        );
    };

    const submitMultiContainerEvaluation = async (
        selectedContainerIds: string[],
        containerMetadata: {[key: string]: any},
        contentText: string,
        dispatch: any
    ) => {
        if (!selectedContainerIds || selectedContainerIds.length === 0) {
            return;
        }

        dispatch({ type: "INIT_CONTAINERS", containerIds: selectedContainerIds });

        const evaluationPromises = selectedContainerIds.map(async (containerId) => {
            try {
                const entityRef: EntityRef = {
                    type: EntityType.Container,
                    id: containerId,
                    version: containerMetadata?.[containerId]?.version || 0
                };

                const response = (await context.getContentEvaluationConfigurationAPI().submitContentEvaluation(
                    IContentEvaluation.SubmitContentEvaluationInput.create({
                        entityRef: entityRef,
                        contentText: contentText
                    })
                )).data as unknown as APIResponse<IContentEvaluation.SubmitContentEvaluationOutput>;

                if (response.data && typeof response.data.isOk === "function" && response.data.isOk()) {
                    dispatch({ type: "SET_RESULT", containerId, result: { data: response.data.data } });
                } else if (response.data && typeof response.data.isErr === "function" && response.data.isErr()) {
                    const errorMessage = response.data.err?.message || "API returned error";
                    dispatch({ type: "SET_ERROR", containerId, error: errorMessage });
                } else {
                    if (response.data && typeof response.data === "object" &&
                        ("findings" in response.data || "totalFindings" in response.data)) {
                        dispatch({ type: "SET_RESULT", containerId, result: { data: response.data as unknown as IContentEvaluation.SubmitContentEvaluationOutput } });
                    } else {
                        dispatch({ type: "SET_ERROR", containerId, error: `Unexpected response structure: ${JSON.stringify(response.data)}` });
                    }
                }

                return { containerId, success: true, response };

            } catch (error) {
                dispatch({ type: "SET_ERROR", containerId, error: error.message || "Unknown error" });
                return { containerId, success: false, error: error.message || "Unknown error" };
            }
        });

        await Promise.allSettled(evaluationPromises);
    };

    const clearResults = () => {
        setEvaluationResults(null);
        setFlashbarProps(undefined);
    };

    return {
        evaluationResults,
        flashbarProps,
        contentEvaluationConfig,
        isLoadingConfig,
        isSubmitting: submitContentEvaluationRunner.status === "Running",
        submitSingleEvaluation,
        submitMultiContainerEvaluation,
        clearResults,
        setFlashbarProps
    };
};
